import { useState } from "react";
import { AlertTriangle } from "lucide-react";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { StatusBadge } from "../../features/web3/forex/components/StatusBadge";
import { CopyHash } from "../../features/web3/forex/components/CopyHash";

import { formatDateTime, formatInteger, formatUsd } from "../../lib/format";
import { cn } from "../../lib/utils";
import {
  TRANSFER_DIRECTIONS,
  TRANSFER_STATUS_OPTIONS,
  useTransferSummary,
  useTransfers,
} from "./api";
import { FilterBar, FilterField, PAGE_SIZE_OPTIONS, PagePager, orDash } from "./parts";

const EMPTY = {
  username: "",
  loginId: "",
  status: "",
  startDate: "",
  endDate: "",
  order: "desc",
  pageSize: "50",
};

const ORDER_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const COLUMNS = [
  { label: "Date" },
  { label: "User" },
  { label: "Type" },
  { label: "From" },
  { label: "To" },
  { label: "Amount", align: "right" },
  { label: "MT5 login" },
  { label: "Ticket" },
  { label: "Tranx ID" },
  { label: "Status" },
  { label: "Failure reason" },
];

const Stat = ({ label, value }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono text-foreground">{value}</span>
  </div>
);

// One card per direction. The amounts under a direction are equal by
// construction, so the total amount is shown once. uniqueByLoginId is absent
// (not zero) on the GUSD directions, since no trading account is involved.
function SummaryCards({ summary, direction, onPick }) {
  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {TRANSFER_DIRECTIONS.map((d) => {
          const s = summary?.[d.summaryKey];
          return (
            <button
              key={d.value}
              type="button"
              onClick={() => onPick(d.value)}
              className={cn(
                "space-y-1.5 rounded-lg border p-4 text-left transition-colors",
                direction === d.value
                  ? "border-ginox-green/50 bg-ginox-green/[0.06]"
                  : "border-border/70 bg-white/[0.02] hover:bg-white/[0.04]",
              )}
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {d.label}
              </div>
              <div className="font-mono text-xl text-foreground">
                {s ? formatUsd(s.total.amount) : "—"}
              </div>
              <Stat label="Transfers" value={s ? formatInteger(s.total.transfers) : "—"} />
              <Stat label="Users" value={s ? formatInteger(s.uniqueByUsername.users) : "—"} />
              {d.mt5 && (
                <Stat label="MT5 logins" value={s?.uniqueByLoginId ? formatInteger(s.uniqueByLoginId.logins) : "—"} />
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Totals count completed transfers only; the status filter does not apply to them.
      </p>
    </div>
  );
}

export default function Transfers() {
  const [direction, setDirection] = useState("forex-to-mt5");
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);

  // Only the two MT5 routes have a trading account to filter on.
  const mt5 = TRANSFER_DIRECTIONS.find((d) => d.value === direction)?.mt5;
  const { loginId, ...rest } = applied;
  const shared = {
    username: rest.username.trim(),
    ...(mt5 ? { loginId: loginId.trim() } : {}),
    startDate: rest.startDate,
    endDate: rest.endDate,
  };
  const q = useTransfers(direction, { ...rest, ...shared, page });
  const summary = useTransferSummary(shared);
  const data = q.data;

  const pickDirection = (d) => {
    setDirection(d);
    setPage(1);
  };

  return (
    <QueueLayout
      eyebrow="Forex"
      title="Transfer"
      accent="history"
      description="Money users moved between their own balances. A row means money moved: refused attempts write nothing, and history starts at deploy."
      statusOptions={TRANSFER_DIRECTIONS}
      status={direction}
      onStatus={pickDirection}
      isFetching={q.isFetching || summary.isFetching}
      onRefresh={() => {
        q.refetch();
        summary.refetch();
      }}
    >
      <FilterBar
        onApply={() => {
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(EMPTY);
          setApplied(EMPTY);
          setPage(1);
        }}
      >
        <FilterField label="Username" name="username" draft={draft} setDraft={setDraft} placeholder="All users" />
        {mt5 && (
          <FilterField label="MT5 login" name="loginId" draft={draft} setDraft={setDraft} placeholder="All accounts" />
        )}
        <FilterField label="Status" name="status" options={TRANSFER_STATUS_OPTIONS} draft={draft} setDraft={setDraft} />
        <FilterField label="From date" name="startDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="To date" name="endDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="Order" name="order" options={ORDER_OPTIONS} draft={draft} setDraft={setDraft} />
        <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {summary.error ? (
        <ErrorBanner error={summary.error} onRetry={() => summary.refetch()} />
      ) : (
        <SummaryCards summary={summary.data} direction={direction} onPick={pickDirection} />
      )}

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={COLUMNS}
            isLoading={q.isLoading}
            rows={data?.data}
            renderRow={(row) => {
              // Stranded: both legs failed and the money is in neither place.
              // A support case, so it must not blend into the list.
              const stranded = row.status === "stranded";
              return (
                <tr
                  key={row.tranx_id}
                  className={stranded ? "bg-red-500/[0.08]" : "hover:bg-white/[0.02]"}
                >
                  <Td className="whitespace-nowrap font-mono text-muted-foreground">
                    {formatDateTime(row.createdAt)}
                  </Td>
                  <Td className="font-mono">{row.username}</Td>
                  <Td className="whitespace-nowrap text-xs">{row.type}</Td>
                  <Td className="text-xs">{row.fromWallet}</Td>
                  <Td className="text-xs">{row.toWallet}</Td>
                  <Td align="right" className="font-mono">
                    {Number(row.amount).toFixed(2)}
                  </Td>
                  <Td className="font-mono">{orDash(row.loginId)}</Td>
                  <Td className="font-mono">{orDash(row.ticket)}</Td>
                  <Td>
                    <CopyHash value={row.tranx_id} head={10} tail={6} />
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      {stranded && <AlertTriangle className="h-3.5 w-3.5 text-red-400" />}
                      <StatusBadge status={row.status?.toUpperCase()} />
                    </span>
                  </Td>
                  <Td className={stranded ? "text-xs text-red-400" : "text-xs text-muted-foreground"}>
                    {orDash(row.failureReason)}
                  </Td>
                </tr>
              );
            }}
          />
          <PagePager data={data} onChange={setPage} label="transfers" />
        </>
      )}
    </QueueLayout>
  );
}
